/**
 * pendingSaveStore — typed store for the "save this trip after login" blob.
 *
 * When an anonymous user clicks Save, we stash the in-progress trip in
 * localStorage, open the auth modal, and after the user signs in (email,
 * password, or OAuth redirect), we resume the save automatically.
 *
 * The previous implementation had a plain localStorage string key
 * (`tb_pending_save`) with inline try/catch error handling and no shape
 * contract. It also had the bug that the post-login effect didn't gate
 * on the session being actually ready for DB writes (see H-103).
 *
 * This module:
 * - Encapsulates the key name so it can't drift.
 * - Encodes the shape so the consumer can't read a stale schema by
 *   accident.
 * - Routes every error through reportError (via safeStorage).
 * - Does NOT deal with the session-ready gating — that's useSessionReady.
 */

import { safeStorage } from './safeStorage';
import type { RankedPlace } from '@/hooks/useLLM';

const STORAGE_KEY = 'tb_pending_save';
// Matches the sign-in link and code lifetime: a save survives an emailed sign-in, not a sign-in days later.
const PENDING_SAVE_TTL_MS = 60 * 60 * 1000;

// Re-declared locally to avoid importing SearchData from App.tsx.
// The only part of SearchData we care about for saves is the fields
// used by saveItinerary.
export interface PendingSearchData {
  location: string;
  timeAvailable: string;
  transport: string;
  vibes: string[];
  customVibe?: string;
  coords?: { lat: number; lng: number };
  country?: string;
}

export interface PendingSavePayload {
  searchData: PendingSearchData;
  places: RankedPlace[];
  /** Epoch ms; entries older than PENDING_SAVE_TTL_MS are dropped rather than resumed. */
  savedAt?: number;
  // Version tag so a future breaking change can be detected and the
  // stale entry dropped instead of resumed with the wrong shape.
  v: 1;
}

export function readPendingSave(): PendingSavePayload | null {
  const raw = safeStorage.getJSON<PendingSavePayload>(STORAGE_KEY);
  if (!raw) return null;
  if (raw.v !== 1) {
    // Schema drift — drop it.
    safeStorage.remove(STORAGE_KEY);
    return null;
  }
  if (!raw.searchData || !Array.isArray(raw.places)) {
    safeStorage.remove(STORAGE_KEY);
    return null;
  }
  if (!raw.savedAt || Date.now() - raw.savedAt > PENDING_SAVE_TTL_MS) {
    safeStorage.remove(STORAGE_KEY);
    return null;
  }
  return raw;
}

export function writePendingSave(
  searchData: PendingSearchData,
  places: RankedPlace[],
): boolean {
  const payload: PendingSavePayload = { searchData, places, savedAt: Date.now(), v: 1 };
  return safeStorage.setJSON(STORAGE_KEY, payload);
}

export function clearPendingSave(): void {
  safeStorage.remove(STORAGE_KEY);
}
